// React
import { memo, useMemo } from 'react';

// Components
import { BaseImage, BaseImagePreview } from 'components';

// Antd
import { Button } from 'antd';
import { CloseOutlined } from '@ant-design/icons';

// Interfaces
import { IBackOfficeBasePhotoUploader } from './interfaces';

// i18n
import { useTranslation } from 'react-i18next';

// Constant
import { FILE_IMAGE_EXTENSIONS, FILE_SIZE_0_3_MB, FORMAT_BYTES } from 'constant/file.constant';

// Custom Hook
import { useUploader } from 'hooks';

// Styles
import './styles.scss';

const BackOfficeBasePhotoUploader = memo(
  ({
    image,
    isEditable,
    withoutCard,
    onChange,
    error,
    errorFieldName,
    withRemove,
    maximumFileSize,
  }: IBackOfficeBasePhotoUploader) => {
    // Hook
    const { t } = useTranslation();
    const fileSize = useMemo((): number => {
      return maximumFileSize || FILE_SIZE_0_3_MB;
    }, [maximumFileSize]);
    const { uploader_Input, uploader_onClickSingleFile } = useUploader({
      acceptedExtensions: FILE_IMAGE_EXTENSIONS,
      maximumFileSize: fileSize,
    });

    return (
      <>
        {/* Plain HTML Input File */}
        {uploader_Input(onChange)}

        <div className="back-office-base-photo-uploader">
          <div className={`card ${withoutCard ? 'without-card' : ''}`}>
            {/* Remove Button */}
            {withRemove && image && (
              <div className="remove-button">
                <CloseOutlined
                  style={{ cursor: 'pointer', color: '#ff5858', fontSize: 18 }}
                  onClick={event => {
                    event.stopPropagation();

                    onChange(null);
                  }}
                />
              </div>
            )}

            {/* Render Image */}
            {image ? (
              <div className="cursor-pointer">
                <BaseImagePreview
                  buttons={{ showNextButton: false, showPrevButton: false }}
                  thumbnails={{ showThumbnails: false }}
                  settings={{ disableKeyboardControls: true, disableWheelControls: true, disablePanzoom: true }}
                >
                  <a href={image}>
                    <img src={image} alt={image} />
                  </a>
                </BaseImagePreview>
              </div>
            ) : (
              <BaseImage isImage image="back-office/image-big-empty" isError={Boolean(error)} />
            )}

            {/* Render Error */}
            {error && (
              <div className="my-1">
                <p className="text-danger">
                  {errorFieldName ? `${t(errorFieldName || '')} ${t('app.validations.required')}` : error}
                </p>
              </div>
            )}

            {/* Check if devs want to use uploader or just thumbnail, and want to use ant design or the common browser uploader */}
            {isEditable && (
              <>
                <Button
                  type="primary"
                  className={`button mt-4 ${Boolean(error) ? 'danger' : ''}`}
                  onClick={uploader_onClickSingleFile}
                  ghost
                >
                  {t('app.choosePhoto')}
                </Button>
                <p className="font-12 ml-2 mt-2 text-grey-font font-weight-400">
                  {t('app.validations.imageFormatFull')} {FORMAT_BYTES(fileSize)}
                </p>
              </>
            )}
          </div>
        </div>
      </>
    );
  },
);

export { BackOfficeBasePhotoUploader };
